import React from 'react';
import { Link } from 'react-router-dom';
import { snip as snipAt } from './snip';

// WHO SAID IT — the one attribution renderer, for every surface (2026-08-14).
//
// THE PROBLEM THIS CLOSES. Four surfaces rendered an object's attribution four
// different ways, and two of them rendered it not at all:
//
//   Thread.js      a local `Byline`: /actor links, comma-joined, italic
//                  "unattributed" when empty. The only correct one.
//   Read.js        `rd-chip` chips, full text, NO empty state — a document
//                  whose author extraction failed renders a blank strip.
//   ObjectDetail   nothing. `authored_by` fell into the generic connections
//                  list, so the object page for a claim — the surface a reader
//                  lands on from a published thread — was the one place that
//                  did not say who said it.
//   StreamRow.js   `by {creator_username}`, which is the ACCOUNT THAT ENTERED
//                  IT, not the author. On a claim decomposed from a book that
//                  reads as the owner's own assertion. Worse than absence.
//
// lib/threadAttribution.ts already names this as the false-absence defect and
// calls out Read.js and StreamRow.js BY NAME. The backend half landed; this is
// the frontend half that never did.
//
// ⚠ THE ORDER IS A CONSTANT, NOT A CONVENTION. `SLOTS` below is the whole
// point of the file. Attribution arrives from four different relations, and
// before this each surface picked its own order or had none — so the same claim
// could read "Hawkins · written with claude-opus-5" here and "claude-opus-5 ·
// Hawkins" there, which invites the reader to mistake the instrument for the
// speaker. One array, one order, every surface.
//
// ⚠ THE "unattributed" FLOOR APPLIES TO THE SPEAKER SLOTS ONLY. An object with
// no `authored_by` and no `asserted_by` says "unattributed" out loud, because
// rendering nothing there is a claim — it makes a quoted assertion read as the
// thread author's own belief. An object with no `written_with` says NOTHING,
// because the absence of a model note asserts nothing about anybody. Absence
// stated where absence misleads; silence everywhere else.

// The slot order, and what each one means to a reader. `key` is the field on
// the `attribution` prop; `speaker` marks the slots the unattributed floor
// covers.
//
// `asserted_by` IS DELIBERATELY HERE BEFORE THE RELATION EXISTS (owner call
// 2026-08-14). It renders nothing until something populates it. The alternative
// — adding the slot when the relation ships — means re-touching all four
// surfaces to insert it in the middle of an order they each hardcoded, which is
// the drift this file exists to prevent. The build brief is
// `worldview-build-brief-attribution-and-note-retrieval.md`.
export const SLOTS = [
  // The actor who MADE the statement in the world. The byline proper.
  { key: 'authored_by', speaker: true, prefix: null },
  // The actor a claim is QUOTED FROM, where the page's author is reporting
  // rather than asserting ("X reported that Y asserted P"). Distinct from
  // authored_by precisely so a reader can tell those apart.
  { key: 'asserted_by', speaker: true, prefix: 'quoting' },
  // The MODEL named as the instrument (DECISIONS.md 2026-08-02, "The MODEL is
  // an actor, the harness is not"). Provenance, not testimony — it says what
  // extracted the text, never who believes it, so it sits after both speakers
  // and carries its own words.
  { key: 'written_with', speaker: false, prefix: 'written with' },
  // The document this was extracted from. Last: it is the weakest attribution
  // claim of the four and the one a reader needs only after the other three.
  { key: 'derived_from', speaker: false, prefix: 'from' },
];

const snip = (s, n = 60) => snipAt(s, n);

// One actor, as a link to its timeline. `content` is the actor's name; actor
// objects are deliberately public (GROUNDED_TYPES), so this link resolves for a
// signed-out reader following a published thread.
function Actor({ actor, linkTo }) {
  return (
    <Link to={`${linkTo}/${actor.id}`} style={{ color: 'inherit' }}>
      {snip(actor.content)}
    </Link>
  );
}

/**
 * `attribution` — `{ authored_by, asserted_by, written_with, derived_from }`,
 * each an array of `{ id, content, object_type }`. Every key is optional; a
 * surface passes what its payload carries and the rest render as nothing.
 *
 * `variant` — `line` (default: one muted line, for a card or a step) or `chip`
 * (the Reader's header strip). Density only; the order and the floor are
 * identical in both, which is the invariant worth keeping.
 *
 * ⚠ `chip` RENDERS A FRAGMENT, NOT A WRAPPER. `.rd-byline` is a flex row whose
 * gap spaces its DIRECT children, and the Reader puts its arrival chips
 * ("⇢ via archive snapshot") in that same row beside the attribution. Wrapping
 * these in a div would nest them one level down and collapse the strip to two
 * items. The caller owns the container; this returns chips to put in it.
 *
 * `showUnattributed` — defaults true. Set false ONLY where the surrounding copy
 * already says who is speaking; a heading written by the thread's author is the
 * live case, and Thread.js suppresses the byline entirely there rather than
 * passing this.
 *
 * `slots` — restrict to a subset of `SLOTS`, still rendered in SLOTS order (a
 * caller cannot reorder, only omit). The live case is the instrument: every
 * claim decomposed from one source carries the same `written_with`, so on a
 * thread or a feed it repeats under every row and distinguishes none of them,
 * while on the focused object page it is a fact about the thing you are looking
 * at. Speaker slots everywhere; provenance where it is about one object.
 *
 * `style` — merged over the variant's own. Exists for ONE reason worth naming:
 * the `line` variant carries a top margin because its home is stacked under a
 * card's content, and StreamRow puts it in a CENTERED FLEX ROW where that
 * margin pushes it out of alignment with the band and chips beside it. Layout
 * belongs to the surface; the slots and the floor do not.
 */
function Byline({ attribution, variant = 'line', showUnattributed = true, slots, className, style }) {
  const a = attribution || {};
  // `derived_from` targets are documents rather than actors, so they route to
  // the object page. Everything else is an actor and routes to its timeline.
  const linkFor = (key) => (key === 'derived_from' ? '/object' : '/actor');

  // Filtered FROM the constant, never rebuilt by the caller — a `slots` prop
  // that accepted its own ordering would hand each surface back exactly the
  // freedom this file exists to take away.
  const active = slots ? SLOTS.filter((s) => slots.includes(s.key)) : SLOTS;

  const filled = active
    .map((slot) => ({ slot, actors: (a[slot.key] || []).filter(Boolean) }))
    .filter((s) => s.actors.length > 0);
  const hasSpeaker = filled.some(({ slot }) => slot.speaker);

  if (filled.length === 0 && !showUnattributed) return null;

  const chip = variant === 'chip';
  const parts = [];

  for (const { slot, actors } of filled) {
    parts.push(
      <span key={slot.key} className={chip ? 'rd-chip' : undefined}>
        {slot.prefix && (
          <span className={chip ? undefined : 'trust-muted'} style={{ fontStyle: 'italic' }}>
            {slot.prefix}{' '}
          </span>
        )}
        {actors.map((actor, i) => (
          <React.Fragment key={actor.id}>
            {i > 0 && ', '}
            <Actor actor={actor} linkTo={linkFor(slot.key)} />
          </React.Fragment>
        ))}
      </span>
    );
  }

  // The floor. Fires when no SPEAKER slot is filled — an object can carry
  // `written_with` and still be unattributed, and saying so beside the model
  // note is exactly right: something extracted it, nobody is on record as
  // having said it.
  if (!hasSpeaker && showUnattributed) {
    parts.unshift(
      <span key="unattributed" style={{ fontStyle: 'italic' }} className={chip ? 'rd-chip' : undefined}>
        unattributed
      </span>
    );
  }

  if (parts.length === 0) return null;

  if (chip) return <>{parts}</>;

  return (
    <div
      className={className || 'trust-muted'}
      style={{ fontSize: 'var(--wv-fs-xs)', marginTop: 'var(--wv-sp-1)', ...style }}
    >
      {parts.map((p, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span style={{ opacity: 0.5 }}> · </span>}
          {p}
        </React.Fragment>
      ))}
    </div>
  );
}

export default Byline;
